

















































import Vue from 'vue';
import { Component, ProvideReactive, Watch, Prop } from 'vue-property-decorator';
import { track } from 'frontend/utils/analytics';
import { AlgoliaResults, SearchParams } from 'frontend/types/search';
import algoliasearch from 'algoliasearch/lite';

import SuggestedQueries from 'frontend/components/search/SuggestedQueries.vue';
import SearchQueryResults from 'frontend/components/search/SearchQueryResults.vue';
import SearchInput from 'frontend/components/search/SearchInput.vue';

import { defer, asyncTimeout } from 'frontend/utils/timing';
import { scrollToAnchorName, lockScroll } from 'frontend/utils/scroll';
import { dumbSlugifySearchQuery } from 'frontend/utils/formatting';
import { getTestId } from 'frontend/utils/test-id';
import { Optional } from 'frontend/types';

@Component({
  components: {
    SearchInput,
    SuggestedQueries,
    SearchQueryResults,
  },
  // see https://github.com/kaorun343/vue-property-decorator/issues/277
  inject: [],
})
export default class SearchWidget extends Vue {
  @ProvideReactive() private coursesHits: any[] = [];
  @ProvideReactive() private csgHits: any[] = [];
  @ProvideReactive() private searchQuery = '';
  @ProvideReactive() private courseQueryID?: string = '';
  @ProvideReactive() private csgQueryID?: string = '';

  private activelySearching = false;
  private showSearchResults = false;
  private numLessons = 0;
  private numCourses = 0;
  private numHitsDisplayed = 0;
  private numCSGs = 0;
  private numCSGCourses = 0;
  private fromSuggestedSearch = false;
  private csg: any;
  @Prop(Boolean) private isStaffUser!: boolean;
  @Prop(Boolean) private mobile!: boolean;
  @Prop(String) private analyticsIdentity!: string;
  @Prop(Boolean) private highlightSearch!: boolean;
  @Prop(Boolean) private inHeader!: boolean;
  @Prop(String) private inputElementKey!: string;
  @Prop(String) private algoliaProxyUrl?: string;
  @Prop(String) private readonly testIdPrefix: Optional<string>;

  private searchClient: algoliasearch.Client | undefined = undefined;
  private showHighlight = false;
  private showHighlightAlert = false;

  private get componentKey(): string {
    return this.inputElementKey;
  }

  private get testId(): string {
    return getTestId(this.testIdPrefix, 'search_widget');
  }

  private onWheel(e: Event) {
    const { clientHeight: height, scrollHeight, scrollTop } = e.currentTarget as HTMLElement;
    const { deltaY } = e as WheelEvent;

    if (deltaY !== 0) {
      const up = deltaY < 0;
      // prevent scrolling when top/bottom edge of the content is reached
      // to prevent the whole page from scrolling
      if ((up && scrollTop === 0) || (!up && scrollTop + height === scrollHeight)) {
        e.preventDefault();
      }
    }
  }

  private useCustomQuery(query: string): void {
    this.$emit('custom-search');
    this.fromSuggestedSearch = false;
    this.searchQuery = query;
  }

  private useSuggestedQuery(query: string): void {
    this.$emit('suggested-search');
    (this.$refs.searchInput as SearchInput).setValue(query);
    this.searchQuery = query;
    this.fromSuggestedSearch = true;
  }

  private async startSearching() {
    this.$emit('started-searching');
    if (!this.inHeader) {
      // 'search-anchor' is in CoursesHeader.vue
      await scrollToAnchorName('search-anchor');
    }
    this.activelySearching = true;
    defer(() => window.addEventListener('click', this.stopSearching));
  }

  @Watch('highlightSearch')
  private async highlightSearchChanged() {
    await scrollToAnchorName('search-highlight');
    this.showHighlight = this.highlightSearch;
    if (this.showHighlight) {
      lockScroll(true);
      this.activelySearching = true;
      await asyncTimeout(200);
      this.showHighlightAlert = true;
    } else {
      await this.startSearching();
    }
  }

  private handleHideHighlight() {
    lockScroll(false);
    this.$store.dispatch('recommendedCourseKeywords/highlightSearch', false);
  }

  private getAlgoliaSearchResults() {
    const params: SearchParams = {
      clickAnalytics: true,
      userToken: this.analyticsIdentity,
    };

    const coursesSearchQueries = [
      {
        params,
        indexName: 'brilliant-course-search-groups',
        query: this.searchQuery,
      },
      {
        params,
        indexName: 'brilliant-courses',
        query: this.searchQuery,
      },
    ];

    this.getSearchClient().search(coursesSearchQueries, (err, algoliaResults: AlgoliaResults) => {
      if (err) throw err;
      const results = algoliaResults.results;
      this.csgHits = results[0].hits;
      this.coursesHits = results[1].hits;
      this.showSearchResults = true;

      this.csgQueryID = results[0].queryID;
      this.courseQueryID = results[1].queryID;

      this.numCourses = this.coursesHits.length;
      this.numHitsDisplayed = this.numLessons;

      if (this.csgHits.length > 0) {
        this.csg = this.csgHits[0];
        this.numCSGs = 1;
        this.numCSGCourses = this.csg.search_group_courses.length;
        this.numHitsDisplayed += this.numCSGCourses;
      } else {
        this.csg = null;
        this.numCSGs = 0;
        this.numCSGCourses = 0;
        this.numHitsDisplayed += this.numCourses;
      }

      track('search_result', this.searchEventDetails, {});
    });
  }

  private get searchEventDetails() {
    let resultType = 'no_results';

    if (this.numCSGs === 1) {
      resultType = 'search_group';
    } else if (this.numCourses > 0) {
      resultType = 'courses';
    }

    return {
      search_type: 'course_search',
      is_course_search: true,
      query: dumbSlugifySearchQuery(this.searchQuery),
      filters: null,
      from_suggested_search: this.fromSuggestedSearch,
      search_result_type: resultType,
      hits: this.numHitsDisplayed,
      path: window.location.pathname,
      custom_search_card_type: null,
      course_query_id: this.courseQueryID,
      course_search_group_query_id: this.csgQueryID,
    };
  }

  private stopSearching() {
    this.activelySearching = false;
    const searchInput = this.$refs.searchInput as SearchInput;
    if (searchInput) {
      searchInput.blur();
    }
    window.removeEventListener('click', this.stopSearching);
  }

  @Watch('searchQuery')
  private onSearchQueryChanged(newQuery: string): void {
    if (newQuery.trim().length > 0) {
      this.getAlgoliaSearchResults();
    } else {
      this.showSearchResults = false;
    }
  }

  private adjustSearchDropdownHeight(): void {
    window.requestAnimationFrame(() => {
      const searchDropdown = this.$refs.searchDropdown as HTMLElement;
      if (!searchDropdown) {
        return;
      }

      const margin = parseInt(window.getComputedStyle(searchDropdown).marginBottom, 10);
      const top = searchDropdown.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;

      searchDropdown.style.maxHeight = `${windowHeight - top - margin}px`;
    });
  }

  @Watch('activelySearching')
  private onActivelySearchingChanged(newValue: boolean): void {
    const eventTypes = ['scroll', 'resize'];
    if (newValue) {
      eventTypes.forEach(e => window.addEventListener(e, this.adjustSearchDropdownHeight));
      Vue.nextTick(this.adjustSearchDropdownHeight);
    } else {
      eventTypes.forEach(e => window.removeEventListener(e, this.adjustSearchDropdownHeight));
    }
  }

  private getSearchClient(): algoliasearch.Client {
    if (!this.searchClient) {
      let options = {};
      if (this.algoliaProxyUrl) {
        const [protocol, hostAndPath] = this.algoliaProxyUrl.split('//');
        options = {
          protocol,
          hosts: {
            read: [hostAndPath],
            write: [hostAndPath],
          },
        };
      }

      this.searchClient = algoliasearch(
        'XRSHSPDRBU', // appID
        'de2a5a4e3450e1ba35822a37457eac88', // apiKey
        options,
      );
    }
    return this.searchClient;
  }

  private mounted() {
    this.getSearchClient().setExtraHeader('X-Algolia-User-Token', this.analyticsIdentity);
  }
}
